/* eslint-disable @nx/enforce-module-boundaries */
import { Input, InputGroup } from '@mybridge/ui/input';
import { List, ListItem } from '@mybridge/ui/list';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@mybridge/ui/popover';
import { useEffect, useRef, useState } from 'react';
import { useDisclosure } from '@mybridge/ui/hooks';
import SectionLoader from '../common/loader/sectionLoader';
import { formatCommaSeparated } from 'v4/lib/commons';

export const CityStateDropdown = ({
  value,
  defaultValue,
  onChange,
  countryCode,
  ...props
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [places, setPlaces] = useState([]);
  const [selected, setSelected] = useState(value ?? defaultValue ?? null);
  const [search, setSearch] = useState(
    selected ? formatCommaSeparated([selected.city, selected.state]) : ''
  );
  const tid = useRef(null);
  const disc = useDisclosure();

  // sync external value -> local selected/search
  useEffect(() => {
    if (value && (JSON.stringify(value) !== JSON.stringify(selected))) {
      setSelected(value);
      setSearch(formatCommaSeparated([value.city, value.state]));
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (tid.current) clearTimeout(tid.current);
    };
  }, []);

  // fetch places from server
  const fetchPlaces = async (str) => {
    try {
      const res = await fetch(
        `${process.env.BASE_API_URL}api/v1/location/google/search?address=${encodeURIComponent(
          str
        )}&country_code=${encodeURIComponent(countryCode ?? '')}`
      );
      const places_ = await res?.json?.();
      const arr = places_ ?? [];
      setPlaces(arr);
      if (arr.length > 0) {
        disc.onOpen();
      } else {
        disc.onClose();
      }
    } catch (e) {
      console.error('CityStateDropdown fetch error', e);
      setPlaces([]);
      disc.onClose();
    }
  };

  const handleSearch = (str) => {
    setSearch(str);
    if (tid.current) clearTimeout(tid.current);
    tid.current = setTimeout(() => {
      const term = (str ?? '').trim();
      if (term.length) {
        fetchPlaces(term);
      } else {
        setPlaces([]);
        disc.onClose();
      }
    }, 300);
  };

  const handleSelect = (item) => {
    setSelected(item);
    const formatted = formatCommaSeparated([item.city, item.state]);
    setSearch(formatted);
    setPlaces([]);
    onChange?.(item);
    disc.onClose();
  };

  // Close popover when clicking/touching outside of the input + popover content.
  // Also reset typed text to last selected value (if any).
  useEffect(() => {
    function onDocPointer(e) {
      const target = e.target;
      if (
        !inputRef.current?.contains(target) &&
        !containerRef.current?.contains(target)
      ) {
        // reset typed input to selected (or empty)
        setSearch(selected ? formatCommaSeparated([selected.city, selected.state]) : '');
        setPlaces([]);
        disc.onClose();
      }
    }

    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);

    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [selected, disc]);

  return (
    <Popover
      {...disc}
      matchWidth={true}
      initialFocusRef={inputRef}
      placement="bottom-start"
      isOpen={disc.isOpen}
    >
      <PopoverTrigger>
        <InputGroup>
          <Input
            ref={inputRef}
            sx={{ textTransform: 'capitalize' }}
            onFocus={() => {
              // If user focuses and there's a typed search, fetch (and open if results)
              if ((search ?? '').trim().length) {
                handleSearch(search);
              } else if (places?.length > 0) {
                disc.onOpen();
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Escape') {
                disc.onClose();
                setSearch(selected ? formatCommaSeparated([selected.city, selected.state]) : '');
              }
            }}
            value={search ?? ''}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Type to search..."
            {...props}
          />
        </InputGroup>
      </PopoverTrigger>

      {/* Only render popover content when open, there is text and places */}
      {disc.isOpen && places?.length > 0 && (search ?? '').length > 0 ? (
        <PopoverContent
          ref={containerRef}
          p={0}
          w="100%"
          maxH="200px"
          overflowY="auto"
        >
          <PopoverBody p={0}>
            {places?.length > 0 ? (
              <List>
                {places.map((c, idx) => (
                  <ListItem
                    key={idx}
                    _hover={{ bg: 'gray.100' }}
                    px={4}
                    py={2}
                    cursor="pointer"
                    onClick={() => handleSelect(c)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSelect(c);
                      }
                    }}
                  >
                    {c.city}, {c.state}
                  </ListItem>
                ))}
              </List>
            ) : (
              <SectionLoader />
            )}
          </PopoverBody>
        </PopoverContent>
      ) : null}
    </Popover>
  );
};
